import type { ReactNode } from 'react';
import {
  BODY_FRAMES,
  BUILDS,
  MAX_ACCESSORIES,
  SKIN_TONES,
  type Appearance,
  type CharacterOption,
} from '@london-life/contracts';
import { SKIN_TONE_COLOURS } from '@london-life/game/appearance';

// The look choices, shared by the character creator and the profile's look editor.

// one choice among a few, as large buttons; the selected one is announced to screen readers
export function Choices<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | null;
  options: { value: T; label: ReactNode; hint?: ReactNode }[];
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="life-choices">
      <legend>{label}</legend>
      <div>
        {options.map((option) => (
          <button
            type="button"
            key={option.value}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
          >
            <strong>{option.label}</strong>
            {option.hint && <small>{option.hint}</small>}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

const fitsFrame = (option: CharacterOption, frame: Appearance['frame']) =>
  option.frames.length === 0 || option.frames.includes(frame);

// changing the frame keeps every choice that still fits, and swaps the rest for the first that does; a part the
// player already wears (`keep`) stays even when the catalogue no longer offers it
export function withFrame(
  look: Appearance,
  frame: Appearance['frame'],
  options: CharacterOption[],
  keep?: Appearance,
): Appearance {
  const fits = (category: CharacterOption['category'], key: string | null) =>
    key === null ||
    options.some((option) => option.category === category && option.key === key && fitsFrame(option, frame));
  const first = (category: CharacterOption['category']) =>
    options.find((option) => option.category === category && fitsFrame(option, frame))?.key ?? '';
  return {
    ...look,
    frame,
    hair: fits('hair', look.hair) || keep?.hair === look.hair ? look.hair : first('hair'),
    outfit: fits('outfit', look.outfit) || keep?.outfit === look.outfit ? look.outfit : first('outfit'),
    facialHair: fits('facial_hair', look.facialHair) || keep?.facialHair === look.facialHair ? look.facialHair : null,
    accessories: look.accessories.filter((item) => fits('accessory', item) || keep?.accessories.includes(item)),
  };
}

// body, build and skin: who the character is
export function BodyFields({
  look,
  onChange,
  options,
  keep,
}: {
  look: Appearance;
  onChange: (look: Appearance) => void;
  options: CharacterOption[];
  keep?: Appearance;
}) {
  return (
    <>
      <Choices
        label="Body"
        value={look.frame}
        options={(Object.keys(BODY_FRAMES) as Appearance['frame'][]).map((value) => ({
          value,
          label: BODY_FRAMES[value],
        }))}
        onChange={(frame) => onChange(withFrame(look, frame, options, keep))}
      />
      <Choices
        label="Build"
        value={look.build}
        options={(Object.keys(BUILDS) as Appearance['build'][]).map((value) => ({ value, label: BUILDS[value] }))}
        onChange={(build) => onChange({ ...look, build })}
      />
      <fieldset className="life-swatches">
        <legend>Skin tone</legend>
        <div>
          {Array.from({ length: SKIN_TONES }, (_, index) => index + 1).map((tone) => (
            <button
              type="button"
              key={tone}
              aria-label={`Skin tone ${tone} of ${SKIN_TONES}`}
              aria-pressed={look.skinTone === tone}
              style={{ background: SKIN_TONE_COLOURS[tone - 1] }}
              onClick={() => onChange({ ...look, skinTone: tone })}
            />
          ))}
        </div>
      </fieldset>
    </>
  );
}

// hair, outfit and accessories: the styling; `prices` adds what each part costs to change, when it does
export function StyleFields({
  look,
  onChange,
  options,
  keep,
  prices,
}: {
  look: Appearance;
  onChange: (look: Appearance) => void;
  options: CharacterOption[];
  keep?: Appearance;
  prices?: { hair: string; hairColour: string; facialHair: string; outfit: string; accessories: string };
}) {
  const of = (category: CharacterOption['category'], current: (string | null)[] = []) => {
    const offered = options.filter((option) => option.category === category && fitsFrame(option, look.frame));
    // a part the player already wears stays on the list, even if new characters can no longer pick it
    const worn = current.filter((key): key is string => Boolean(key) && !offered.some((option) => option.key === key));
    return [...worn.map((key) => ({ key, label: key.replace(/_/g, ' ') })), ...offered];
  };
  const toggleAccessory = (accessory: string) =>
    onChange({
      ...look,
      accessories: look.accessories.includes(accessory)
        ? look.accessories.filter((item) => item !== accessory)
        : [...look.accessories, accessory].slice(0, MAX_ACCESSORIES),
    });
  const label = (text: string, price?: string) => (price ? `${text} · ${price}` : text);
  return (
    <>
      <Choices
        label={label('Hair', prices?.hair)}
        value={look.hair}
        options={of('hair', [keep?.hair ?? null]).map((option) => ({ value: option.key, label: option.label }))}
        onChange={(hair) => onChange({ ...look, hair })}
      />
      <Choices
        label={label('Hair colour', prices?.hairColour)}
        value={look.hairColour}
        options={of('hair_colour', [keep?.hairColour ?? null]).map((option) => ({
          value: option.key,
          label: option.label,
        }))}
        onChange={(hairColour) => onChange({ ...look, hairColour })}
      />
      <Choices
        label={label('Facial hair', prices?.facialHair)}
        value={look.facialHair ?? 'none'}
        options={[
          { value: 'none', label: 'None' },
          ...of('facial_hair', [keep?.facialHair ?? null]).map((option) => ({
            value: option.key,
            label: option.label,
          })),
        ]}
        onChange={(value) => onChange({ ...look, facialHair: value === 'none' ? null : value })}
      />
      <Choices
        label={label('Outfit', prices?.outfit)}
        value={look.outfit}
        options={of('outfit', [keep?.outfit ?? null]).map((option) => ({ value: option.key, label: option.label }))}
        onChange={(outfit) => onChange({ ...look, outfit })}
      />
      <fieldset className="life-choices">
        <legend>{label(`Accessories (up to ${MAX_ACCESSORIES})`, prices?.accessories)}</legend>
        <div>
          {of('accessory', keep?.accessories ?? []).map((option) => (
            <button
              type="button"
              key={option.key}
              aria-pressed={look.accessories.includes(option.key)}
              onClick={() => toggleAccessory(option.key)}
            >
              <strong>{option.label}</strong>
            </button>
          ))}
        </div>
      </fieldset>
    </>
  );
}
